import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { BarChart } from '../../components/charts/BarChart';
import { interpolateColors } from '../../components/charts/colors';
import { useAuth } from '../../auth/useAuth';

interface KpiItem {
  label: string;
  value: number;
  type: 'money' | 'count' | 'percent';
}

interface BreakdownItem {
  label: string;
  value: number;
}

interface HrStats {
  kpis: KpiItem[];
  leave_by_type: BreakdownItem[];
  attendance_this_week: BreakdownItem[];
}

const GREEN_RAMP_STOPS = ['#60aa45', '#3f7a29', '#2c591c'];
// Attendance-this-week bars are a day sequence, not a ranked category, so a
// single flat color rather than an ordinal ramp — see Dashboard.tsx's
// original comment.
const ATTENDANCE_COLOR = '#2a78d6';

export function HrDashboard() {
  const { hasPermission } = useAuth();

  const [stats, setStats] = useState<HrStats | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<HrStats>('/analytics/hr/stats')
      .then(({ data }) => setStats(data))
      .catch((err: ApiError) => setError(err.message || 'Could not load the dashboard.'));
  }, []);

  const kpi = (label: string) => stats?.kpis.find((k) => k.label === label);
  const fmtKpi = (label: string) => kpi(label)?.value ?? '—';

  const reportLink = (permission: string, to: string) =>
    hasPermission(permission) && (
      <div className="chart-card-footer">
        <Link to={to}>View full report →</Link>
      </div>
    );

  return (
    <>
      <header className="page-header">
        <h1>Human Resources Dashboard</h1>
      </header>
      <p className="muted">A snapshot of staff headcount, leave, and attendance.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {!error && !stats && <p className="muted">Loading…</p>}

      {stats && (
        <>
          <div className="stat-grid">
            {['Staff Headcount', 'Pending Leave Requests', 'Clocked In Now'].map((label) => (
              <div className="card stat" key={label}>
                <span className="stat-label">{label}</span>
                <span className="stat-value">{fmtKpi(label)}</span>
              </div>
            ))}
          </div>

          <div className="chart-grid">
            <div className="card">
              <div className="chart-card-header">
                <h2>Leave This Month</h2>
              </div>
              <div className="chart-card-hero">{fmtKpi('Pending Leave Requests')} pending</div>
              <BarChart
                data={stats.leave_by_type.map((a) => ({ label: a.label, values: [a.value] }))}
                seriesColors={interpolateColors(GREEN_RAMP_STOPS, stats.leave_by_type.length)}
                formatValue={(n) => String(n)}
              />
              {reportLink('hr.reports.view', '/reports/hr/leave')}
            </div>

            <div className="card">
              <div className="chart-card-header">
                <h2>Attendance This Week</h2>
              </div>
              <div className="chart-card-hero">{fmtKpi('Clocked In Now')} clocked in now</div>
              <BarChart
                data={stats.attendance_this_week.map((a) => ({ label: a.label, values: [a.value] }))}
                seriesColors={stats.attendance_this_week.map(() => ATTENDANCE_COLOR)}
                formatValue={(n) => String(n)}
              />
              {reportLink('hr.reports.view', '/reports/hr/attendance')}
            </div>
          </div>
        </>
      )}
    </>
  );
}
